import { map, keyBy, isEmpty, reject, sortBy } from "lodash";

import type {
  FilterFetchedItem,
  FilterGroup,
  FilterItemsQueryData,
  FilterOption,
} from "../types";

const isUnknown = (filter: { key?: string }) => filter.key === "_null_";

// TODO: this is checking `.key` on a FilterOption, which only has `.value` -
// so this always evaluates to false. Pre-existing behavior, left unchanged.
const isUnknownOption = (filter: FilterOption) =>
  isUnknown(filter as unknown as { key?: string });

const filterOptions = (
  fetchedFilters: FilterFetchedItem[] | undefined | null
): FilterOption[] => {
  // Safety check: if fetchedFilters is undefined or null, return empty array
  if (!fetchedFilters) {
    return [];
  }

  const nonEmptyFilters = reject(fetchedFilters, (f) => f && isEmpty(f.label));
  const mappedFilters: FilterOption[] = map(nonEmptyFilters, (filter) => ({
    value: filter.key,
    label: isUnknown(filter) ? "Unknown" : filter.label,
    optionalLabel: filter.optionalLabel,
  }));
  const unknowns = mappedFilters.filter((f) => isUnknownOption(f));
  const good = mappedFilters.filter((f) => !isUnknownOption(f));
  const sortedGood = sortBy(good, "label");
  return [...sortedGood, ...unknowns];
};

export type FilterCategoryField =
  | "divisions"
  | "companies"
  | "jobs"
  | "locations"
  | "countries"
  | "teams"
  | "teamTypes";

const FILTER_CATEGORIES: { field: FilterCategoryField; sourceKey: string }[] = [
  { field: "divisions", sourceKey: "divisions" },
  { field: "companies", sourceKey: "company" },
  { field: "jobs", sourceKey: "jobTitle" },
  { field: "locations", sourceKey: "location" },
  { field: "countries", sourceKey: "country" },
  { field: "teams", sourceKey: "teams" },
  { field: "teamTypes", sourceKey: "teamTypes" },
];

export const convertFilterItems = (
  data: FilterItemsQueryData
): Record<FilterCategoryField, FilterOption[]> => {
  const filterItems: Record<FilterCategoryField, FilterOption[]> = {
    divisions: [],
    companies: [],
    jobs: [],
    locations: [],
    countries: [],
    teams: [],
    teamTypes: [],
  };

  // Safety check: if data is undefined or doesn't have filterItems, return empty filterItems
  if (!data || !data.filterItems) {
    return filterItems;
  }

  const fetchedFilterItems: Record<string, FilterGroup> = keyBy(
    data.filterItems,
    "key"
  );

  for (const { field, sourceKey } of FILTER_CATEGORIES) {
    filterItems[field] = filterOptions(
      fetchedFilterItems[sourceKey]?.items || []
    );
  }

  return filterItems;
};

export const convertTagsFilterItems = (data: FilterItemsQueryData) => {
  const filterItems = {
    personTags: [] as FilterOption[],
    personTagTypes: [] as FilterOption[],
  };

  // Safety check: if data is undefined or doesn't have filterItems, return empty filterItems
  if (!data || !data.filterItems) {
    return filterItems;
  }

  const fetchedFilterItems: Record<string, FilterGroup> = keyBy(
    data.filterItems,
    "key"
  );
  filterItems.personTags = filterOptions(
    fetchedFilterItems["personTags"]?.items || []
  );
  filterItems.personTagTypes = filterOptions(
    fetchedFilterItems["personTagTypes"]?.items || []
  );

  return filterItems;
};
